<template>
  <div class="caption-join body">
    <!-- Header -->
    <div class="header-section">
      <img alt="Camera di Commercio" src="@/assets/LG-cc-ti-03.png" />
    </div>

    <!-- Teleprompter viewport -->
    <div
      id="captions-text"
      class="caption tp-viewport"
      ref="captionsContainer"
    >
      <div
        v-for="cap in captions"
        :key="cap.offset"
        class="tp-line"
      >
        {{ cap.text }}
      </div>
    </div>

    <!-- Footer (lingue) -->
    <div class="footer-container">
      <div id="language-select">
        <select v-model="toLanguageCode">
  <!-- Usa i codici che il tuo backend si aspetta -->
  <option value="it">Italiano</option>
  <option value="en">English</option>
  <option value="fr">Français</option>
  <option value="de">Deutsch</option>
  <option value="es">Español</option>
  <option value="ja">日本語-JP</option>
 
</select>
      </div>
      <div class="powered-by">
  <a href="https://www.moresi.com" target="_blank" rel="noopener"><img src="@/assets/pittogrammamoresicom.png" alt="Moresi.com" class="powered-logo" /></a>
  <a href="https://www.moresi.com" target="_blank" rel="noopener" class="powered-link">
    Powered by Moresi.com
  </a>
</div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import * as signalR from '@aspnet/signalr'
import constants from '../lib/constants'

export default {
  name: 'CaptionJoinRealtime',
  props: {
    clientId: { type: String, required: true }
  },
  data () {
    return {
      captions: [],               // SOLO ultime N righe
      connection: null,
      sessionName: 'camera',
      toLanguageCode: 'it',
      lineHeight: 48,             // deve combaciare con .tp-line
      maxKeepLines: 10            // limite righe in DOM
    }
  },
  watch: {
    toLanguageCode: {
      immediate: true,
      handler () { return this.updateLanguageSubscription(this.toLanguageCode) }
    },
    sessionName () { return this.updateLanguageSubscription(this.toLanguageCode) }
  },
  methods: {
    async updateLanguageSubscription (languageCode) {
      if (this.sessionName && languageCode) {
        try {
          await axios.post(`${constants.apiBaseUrl}/api/leave`, { userId: this.clientId })
        } catch (_) {}
        await axios.post(`${constants.apiBaseUrl}/api/selectlanguage`, {
          languageCode,
          userId: this.clientId,
          sessionName: this.sessionName
        })
      }
    },
    _smoothScrollToBottom () {
      const el = this.$refs.captionsContainer
      if (!el) return
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' })
    },
    _cleanupBuffer (captionsMap) {
      while (this.captions.length > this.maxKeepLines) {
        const removed = this.captions.shift()
        if (removed) delete captionsMap[removed.offset]
      }
    }
  },
  async mounted () {
    // query: ?name=room&lang=it&keep=12 (facoltative)
    if (this.$route && this.$route.query) {
      if (this.$route.query.name) this.sessionName = this.$route.query.name
      if (this.$route.query.lang) this.toLanguageCode = this.$route.query.lang.substring(0, 2)
      if (this.$route.query.keep) this.maxKeepLines = Math.max(3, parseInt(this.$route.query.keep, 10) || 10)
    }

    // SignalR
    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(`${constants.apiBaseUrl}/api/${this.clientId}`)
      .build()

    const captionsMap = {}

    this.connection.on('newCaption', (caption) => {
      let local = captionsMap[caption.offset]
      if (!local) {
        local = { offset: caption.offset, text: caption.text }
        this.captions.push(local)
        captionsMap[caption.offset] = local
      } else {
        local.text = caption.text
      }

      this.$nextTick(() => {
        this._cleanupBuffer(captionsMap)
        this._smoothScrollToBottom()
      })
    })

    await this.connection.start()
    await this.updateLanguageSubscription(this.toLanguageCode)
  },
  async beforeDestroy () {
    if (this.connection) {
      try { await this.connection.stop() } catch (_) {}
    }
  }
}
</script>

<style scoped>
/* Layout generale */
.caption-join.body {
  --tp-max-width: 1024px;
  --tp-fade: 48px;              /* altezza sfumatura TOP */
  min-height: 100vh;
  display: grid;
  grid-template-rows: auto 1fr auto; /* header | viewport | footer */
}

/* Header */
.header-section {
  display: grid;
  place-items: center;
  padding: 12px 0 4px 0;
}
.header-section img {
  max-width: 220px;             /* logo più piccolo */
  height: auto;
}

/* Viewport “teleprompter”: fade SOLO in alto */
.tp-viewport {
  margin: 0 auto;
  max-width: var(--tp-max-width);
  height: calc(100vh - 240px);  /* adatta se cambi header/footer */
  overflow: hidden;
  padding: 8px 24px;
  position: relative;
  background: #fff;

  -webkit-font-smoothing: antialiased;
  backface-visibility: hidden;
  transform: translateZ(0);

  /* Mask top-only: in basso NESSUNA sfumatura */
  -webkit-mask-image: linear-gradient(
    to bottom,
    transparent 0,
    #000 var(--tp-fade),
    #000 100%
  );
  mask-image: linear-gradient(
    to bottom,
    transparent 0,
    #000 var(--tp-fade),
    #000 100%
  );
}

/* Tipografia righe */
.tp-line{
  margin: 0;
  line-height: 48px;            /* tienilo allineato a data().lineHeight */
  font-size: 32px;
  color: #243447;
  text-align: left;             /* allineamento a sinistra */
}

/* Footer (lingua) */
.footer-container {
  position: sticky;
  bottom: 0;
  display: grid;
  place-items: center;
  gap: 8px;
  padding: 8px;
  padding-bottom: max(8px, env(safe-area-inset-bottom));
  background: #fff;
}
#language-select select {
  height: 40px;
  width: 120px;
  font-size: 16px;
}

/* Mobile */
@media (max-width: 768px) {
  .header-section img { max-width: 180px; }
  .caption-join.body { --tp-fade: 36px; }
  .tp-line { font-size: 28px; line-height: 44px; }
}
/* Powered by footer */
.powered-by{
  text-align: center;
  font-size: 9pt;
  display: flex;
  align-items: center;    /* centra verticalmente logo + testo */
  justify-content: center;
  gap: 6px;
}
.powered-logo{
  height: 20px;           /* come richiesto */
  width: auto;
  display: inline-block;
  vertical-align: middle;
}

/* Link sempre nero e sottolineato (anche da visitato) */
.powered-by a,
.powered-by a:visited,
.powered-by a:hover,
.powered-by a:active{
  color: #000;
  text-decoration: underline;
  text-underline-offset: 2px;   /* opzionale: più leggibile */
}
</style>
